import { TransactionBuilder } from '@stellar/stellar-sdk';

import {
    clearFeed,
    confirmChannelLink,
    getChannels,
    getFeed,
    getFeedSettings,
    getSep10Challenge,
    getTopics,
    logoutSession,
    markFeedRead,
    refreshTokenPair,
    replaceChannelsTopics,
    sendTestNotification,
    setBindingTopics,
    setFeedEnabled,
    setFeedTimezone,
    setFeedTopics,
    startChannelLink,
    unbindChannel,
    verifySep10,
} from 'api/notifications';

import { LS_NOTIFICATIONS_REFRESH_TOKEN_PREFIX } from 'constants/local-storage';

import EventService from 'services/event.service';

import {
    NotificationChannel,
    NotificationChannelBinding,
    NotificationChannelsReplaceEntry,
    NotificationFeed,
    NotificationTokenPair,
    NotificationTopic,
    NotificationTopicSlug,
} from 'types/notifications';

import AccountService from './account.service';

export enum NotificationsEvent {
    sessionChanged = 'sessionChanged',
    unreadChanged = 'unreadChanged',
    feedEnabledChanged = 'feedEnabledChanged',
    testNotificationSent = 'testNotificationSent',
}

type NotificationsEventPayload = {
    isAuthenticated?: boolean;
    unread?: number;
    enabled?: boolean;
};

// Refresh the access token slightly before it actually expires so an
// in-flight request never races the server-side expiry.
const ACCESS_EXPIRY_SKEW_MS = 30 * 1000;

/**
 * Session and API facade for the aquarius-notifications service.
 *
 * - SEP-10 login reuses the wallet's `AccountService.signTx`.
 * - The short-lived access token is kept in memory only; the one-time refresh
 *   token is stored in localStorage per wallet public key.
 * - Refresh is single-flight: concurrent 401s share one refresh promise, the
 *   rotated pair always replaces the old one atomically.
 */
export default class NotificationsServiceClass {
    event = new EventService<NotificationsEvent, NotificationsEventPayload>();

    private pubKey: string | null = null;
    private timezoneSynced = false;
    private accessToken: string | null = null;
    private accessExpiresAt = 0;
    private refreshPromise: Promise<string> | null = null;

    hasSession(pubKey: string): boolean {
        if (this.pubKey === pubKey && this.accessToken) {
            return true;
        }
        return Boolean(this.getStoredRefreshToken(pubKey));
    }

    get isAuthenticated(): boolean {
        return Boolean(this.pubKey && this.accessToken);
    }

    async login(account: AccountService): Promise<void> {
        const { transaction, network_passphrase: passphrase } = await getSep10Challenge(
            account.accountId(),
        );

        const challengeTx = TransactionBuilder.fromXdr(transaction, passphrase);
        const signedXdr = await account.signAuthenticationChallenge(challengeTx as never);

        const pair = await verifySep10(signedXdr);
        this.applyTokenPair(account.accountId(), pair);
        this.event.trigger({ type: NotificationsEvent.sessionChanged, isAuthenticated: true });
        this.syncTimezone();
    }

    async logout(): Promise<void> {
        const refreshToken = this.pubKey ? this.getStoredRefreshToken(this.pubKey) : null;

        this.clearSession();

        if (refreshToken) {
            try {
                await logoutSession(refreshToken);
            } catch {
                // Logout is fire-and-forget: local state is already cleared.
            }
        }
    }

    clearSession(): void {
        if (this.pubKey) {
            localStorage.removeItem(`${LS_NOTIFICATIONS_REFRESH_TOKEN_PREFIX}${this.pubKey}`);
        }
        this.pubKey = null;
        this.accessToken = null;
        this.accessExpiresAt = 0;
        this.refreshPromise = null;
        this.timezoneSynced = false;
        this.event.trigger({ type: NotificationsEvent.sessionChanged, isAuthenticated: false });
    }

    getTopics(): Promise<NotificationTopic[]> {
        return getTopics();
    }

    getChannels(): Promise<NotificationChannelBinding[]> {
        return this.withAuth(token => getChannels(token));
    }

    startEmailLink(address: string): Promise<void> {
        return this.withAuth(token => startChannelLink(token, NotificationChannel.email, address));
    }

    confirmChannelLink(code: string, emailAddress?: string): Promise<NotificationChannelBinding> {
        return this.withAuth(token => confirmChannelLink(token, code, emailAddress));
    }

    unbindChannel(bindingId: number): Promise<void> {
        return this.withAuth(token => unbindChannel(token, bindingId));
    }

    setBindingTopics(
        bindingId: number,
        topics: string[],
        mode: 'set' | 'subscribe' | 'unsubscribe' = 'set',
    ): Promise<{ topics: string[] }> {
        return this.withAuth(token => setBindingTopics(token, bindingId, topics, mode));
    }

    replaceChannelsTopics(
        entries: NotificationChannelsReplaceEntry[],
    ): Promise<NotificationChannelBinding[]> {
        return this.withAuth(token => replaceChannelsTopics(token, entries));
    }

    getFeed(limit?: number): Promise<NotificationFeed> {
        return this.withAuth(token => getFeed(token, limit)).then(feed => {
            this.event.trigger({
                type: NotificationsEvent.unreadChanged,
                unread: feed.unread,
            });
            return feed;
        });
    }

    clearFeed(): Promise<void> {
        return this.withAuth(token => clearFeed(token)).then(() => {
            this.event.trigger({ type: NotificationsEvent.unreadChanged, unread: 0 });
        });
    }

    getFeedSettings(): Promise<{ enabled: boolean; topics: NotificationTopicSlug[] }> {
        return this.withAuth(token => getFeedSettings(token));
    }

    // Report the browser timezone once per session so server-rendered
    // notifications (email/telegram/discord) show times the same way the
    // site does. Fire-and-forget: a failure only leaves timestamps in UTC.
    private syncTimezone(): void {
        if (this.timezoneSynced) {
            return;
        }
        this.timezoneSynced = true;
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (!timezone) {
            return;
        }
        this.withAuth(token => setFeedTimezone(token, timezone)).catch(() => {
            this.timezoneSynced = false;
        });
    }

    setFeedTopics(
        topics: NotificationTopicSlug[],
    ): Promise<{ enabled: boolean; topics: NotificationTopicSlug[] }> {
        return this.withAuth(token => setFeedTopics(token, topics));
    }

    setFeedEnabled(
        enabled: boolean,
    ): Promise<{ enabled: boolean; topics: NotificationTopicSlug[] }> {
        return this.withAuth(token => setFeedEnabled(token, enabled)).then(result => {
            this.event.trigger({
                type: NotificationsEvent.feedEnabledChanged,
                enabled: result.enabled,
            });
            return result;
        });
    }

    markFeedRead(): Promise<void> {
        return this.withAuth(token => markFeedRead(token)).then(() => {
            this.event.trigger({ type: NotificationsEvent.unreadChanged, unread: 0 });
        });
    }

    sendTestNotification(): Promise<void> {
        return this.withAuth(token => sendTestNotification(token)).then(() => {
            // The backend intentionally persists nothing for tests (events are
            // global), so the in-app bell shows the test locally.
            this.event.trigger({ type: NotificationsEvent.testNotificationSent });
        });
    }

    // Resume a session from the stored refresh token without ever prompting
    // the wallet. Safe to call from passive UI (header bell) on page load.
    async tryResumeSession(pubKey: string): Promise<boolean> {
        if (this.pubKey === pubKey && this.accessToken) {
            return true;
        }

        if (!this.getStoredRefreshToken(pubKey)) {
            return false;
        }

        this.pubKey = pubKey;
        this.accessToken = null;
        try {
            await this.refreshAccessToken();
            this.syncTimezone();
            this.event.trigger({
                type: NotificationsEvent.sessionChanged,
                isAuthenticated: true,
            });
            return true;
        } catch {
            return false;
        }
    }

    // Drop the in-memory session without touching the stored refresh token,
    // e.g. when the wallet disconnects. The next visit resumes silently.
    resetInMemorySession(): void {
        this.pubKey = null;
        this.accessToken = null;
        this.accessExpiresAt = 0;
        this.refreshPromise = null;
        this.timezoneSynced = false;
        this.event.trigger({ type: NotificationsEvent.sessionChanged, isAuthenticated: false });
    }

    // Ensure there is a session for the wallet: reuse the stored refresh token
    // when possible, otherwise run the SEP-10 signing flow.
    async ensureSession(account: AccountService): Promise<void> {
        const resumed = await this.tryResumeSession(account.accountId());
        if (!resumed) {
            await this.login(account);
        }
    }

    private async withAuth<T>(fn: (accessToken: string) => Promise<T>): Promise<T> {
        const token = await this.getValidAccessToken();

        try {
            return await fn(token);
        } catch (error) {
            if (error?.response?.status !== 401) {
                throw error;
            }
            const refreshed = await this.refreshAccessToken();
            return fn(refreshed);
        }
    }

    private async getValidAccessToken(): Promise<string> {
        if (!this.pubKey) {
            throw new Error('Notifications session is not initialized');
        }

        if (this.accessToken && Date.now() < this.accessExpiresAt - ACCESS_EXPIRY_SKEW_MS) {
            return this.accessToken;
        }

        return this.refreshAccessToken();
    }

    private refreshAccessToken(): Promise<string> {
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        const pubKey = this.pubKey;
        const refreshToken = pubKey ? this.getStoredRefreshToken(pubKey) : null;

        if (!pubKey || !refreshToken) {
            this.clearSession();
            return Promise.reject(new Error('Notifications session expired'));
        }

        this.refreshPromise = refreshTokenPair(refreshToken)
            .then(pair => {
                this.applyTokenPair(pubKey, pair);
                return pair.access_token;
            })
            .catch(error => {
                if (error?.response?.status === 401) {
                    this.clearSession();
                }
                throw error;
            })
            .finally(() => {
                this.refreshPromise = null;
            });

        return this.refreshPromise;
    }

    private applyTokenPair(pubKey: string, pair: NotificationTokenPair): void {
        this.pubKey = pubKey;
        this.accessToken = pair.access_token;
        this.accessExpiresAt = new Date(pair.access_expires_at).getTime();
        localStorage.setItem(
            `${LS_NOTIFICATIONS_REFRESH_TOKEN_PREFIX}${pubKey}`,
            pair.refresh_token,
        );
    }

    private getStoredRefreshToken(pubKey: string): string | null {
        return localStorage.getItem(`${LS_NOTIFICATIONS_REFRESH_TOKEN_PREFIX}${pubKey}`);
    }
}
